(() => {
    if (window.__pbCartRangesV3) return;
    window.__pbCartRangesV3 = true;

    const CART_SECTION = 'r_cart';
    const NOTIFICATION_SECTION = 'r_cart-notification';
    const MODE = 'Modo de compra';
    const CITY = 'Ciudad';
    const PACKAGES = 'Cantidad de paquetes';
    const UNITS = 'Unidades solicitadas';
    const TIER = 'Escala aplicada';
    const GROUP = '_pb_group';
    const PRODUCT_ID = '_pb_product_id';
    const QUANTITY_OPTION = '_pb_quantity_option';
    const INCREMENT = '_pb_increment';

    const normalize = (value) => String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();

    const integer = (value) => {
        const parsed = Number.parseInt(String(value ?? '').replace(/[^\d]/g, ''), 10);
        return Number.isInteger(parsed) ? parsed : null;
    };

    const positive = (value) => {
        const parsed = integer(value);
        return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    };

    const hash = (value) => {
        let result = 2166136261;
        for (const character of String(value ?? '')) {
            result ^= character.charCodeAt(0);
            result = Math.imul(result, 16777619);
        }
        return (result >>> 0).toString(36);
    };

    const range = (label) => {
        const text = String(label ?? '').trim();
        const numbers = text.match(/\d[\d.,\s]*/g) || [];
        const from = positive(numbers[0]);
        const open = /o\s*m[aá]s|en\s*adelante|desde\s+ah[ií]|\+|mayor(?:es)?/i.test(text);
        const to = open ? null : positive(numbers[1]);
        return {
            value: text,
            from,
            to,
            valid: Boolean(text)
                && Number.isInteger(from)
                && from > 0
                && (open || (Number.isInteger(to) && to >= from))
        };
    };

    const errorData = async (response) => {
        const data = await response.json();
        if (!response.ok) {
            throw new Error(JSON.stringify({
                message: data.message,
                description: data.description
            }));
        }
        return data;
    };

    customElements.whenDefined('cart-provider').then(() => {
        const Provider = customElements.get('cart-provider');
        if (!Provider || Provider.prototype.__pbCartRangesV3) return;

        const prototype = Provider.prototype;
        prototype.__pbCartRangesV3 = true;
        const nativeAdd = prototype._add;
        const nativeUpdate = prototype._update;

        prototype._pbRequest = function request(route, body) {
            return this._requestCart(route, body).then(errorData);
        };

        prototype._pbCart = async function cart() {
            const response = await fetch('/cart.js', {
                headers: { Accept: 'application/json' }
            });
            if (!response.ok) throw new Error('No se pudo consultar el carrito actual.');
            return response.json();
        };

        prototype._pbProduct = async function product(line) {
            const url = new URL(line.url || `/products/${line.handle || ''}`, location.origin);
            url.search = '';
            url.hash = '';
            if (!url.pathname.endsWith('.js')) {
                url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
            }
            const response = await fetch(url.href, {
                headers: { Accept: 'application/json' }
            });
            if (!response.ok) throw new Error('No se pudo cargar la información del producto.');
            return response.json();
        };

        prototype._pbConfigured = function configured(item) {
            const properties = item?.properties || {};
            return normalize(properties[MODE]) === 'rangos'
                && Boolean(properties[GROUP])
                && Boolean(properties[PRODUCT_ID])
                && Boolean(properties[QUANTITY_OPTION])
                && Boolean(positive(properties[INCREMENT]))
                && Boolean(positive(properties[PACKAGES]));
        };

        prototype._pbContext = function context(item) {
            const properties = item.properties || {};
            const result = {
                group: String(properties[GROUP] || ''),
                productId: String(properties[PRODUCT_ID] || ''),
                quantityOption: String(properties[QUANTITY_OPTION] || ''),
                increment: positive(properties[INCREMENT])
            };
            if (!result.group || !result.productId || !result.quantityOption || !result.increment) {
                throw new Error('La configuración interna del producto está incompleta.');
            }
            return result;
        };

        prototype._pbDerivedGroup = function derivedGroup(line, quantityOption) {
            const properties = line.properties || {};
            const parts = [
                String(line.product_id ?? properties[PRODUCT_ID] ?? ''),
                normalize(properties[MODE]),
                normalize(properties[CITY])
            ];
            (line.options_with_values || []).forEach(({ name, value }) => {
                if (normalize(name) !== normalize(quantityOption)) {
                    parts.push(`${normalize(name)}=${normalize(value)}`);
                }
            });
            return `pb1-${hash(parts.join('\u001f'))}`;
        };

        prototype._pbLines = function lines(cart, context) {
            return (cart.items || []).filter((line) => {
                if (String(line.product_id ?? '') !== context.productId) return false;
                const stored = String(line.properties?.[GROUP] || '');
                if (stored) return stored === context.group;
                return this._pbDerivedGroup(line, context.quantityOption) === context.group;
            });
        };

        prototype._pbTarget = function target(lines, variantId, properties) {
            const candidates = lines.filter(
                (line) => String(line.variant_id ?? line.id) === String(variantId)
            );
            if (!candidates.length) return null;
            const entries = Object.entries(properties || {});
            const score = (line) => entries.reduce((total, [name, value]) => (
                total + (normalize(line.properties?.[name]) === normalize(value) ? 1 : 0)
            ), 0);
            return [...candidates].sort((left, right) => score(right) - score(left))[0];
        };

        prototype._pbChange = function change(key, quantity, properties, sellingPlan, sections = []) {
            const body = { id: key, quantity };
            if (properties !== undefined) body.properties = properties;
            if (sellingPlan !== undefined) body.selling_plan = sellingPlan;
            if (sections.length) {
                body.sections = sections;
                body.sections_url = '/cart';
            }
            return this._pbRequest('cartChange', body);
        };

        prototype._pbConverge = async function converge(item) {
            const quantity = positive(item.quantity);
            if (!quantity) throw new Error('La cantidad configurada no es válida.');

            const context = this._pbContext(item);
            let cart = await this._pbCart();
            let lines = this._pbLines(cart, context);
            let target = this._pbTarget(lines, item.id, item.properties);

            if (!target) {
                await this._pbRequest('cartAdd', { items: [item] });
                cart = await this._pbCart();
                lines = this._pbLines(cart, context);
                target = this._pbTarget(lines, item.id, item.properties);
            }
            if (!target) throw new Error('No se pudo crear la selección configurada.');

            await this._pbChange(
                target.key,
                quantity,
                item.properties,
                item.selling_plan
            );

            for (let guard = 0; guard < 20; guard += 1) {
                cart = await this._pbCart();
                lines = this._pbLines(cart, context);
                target = this._pbTarget(lines, item.id, item.properties);
                if (!target) throw new Error('No se pudo confirmar la selección configurada.');

                const duplicate = lines.find((line) => line.key !== target.key);
                if (!duplicate) return { cart, target, context };
                await this._pbChange(duplicate.key, 0);
            }

            throw new Error('No fue posible consolidar las líneas duplicadas.');
        };

        prototype._pbRender = async function render(result, notificationIds = [], notification = false) {
            const sections = [];
            if (this.features.cart) sections.push(CART_SECTION);
            if (notification && this.features.notification) {
                sections.push(NOTIFICATION_SECTION);
                await this._pbRequest('cartUpdate', {
                    attributes: {
                        notification_items_variant_ids: notificationIds.join(',')
                    }
                });
            }

            let cartData = result.cart;
            if (sections.length) {
                cartData = await this._pbChange(
                    result.target.key,
                    result.target.quantity,
                    undefined,
                    undefined,
                    sections
                );
            }
            this._cleanCartAttributes();
            return cartData;
        };

        prototype._pbOptionNames = function optionNames(product) {
            return (product.options || []).map((option) => (
                typeof option === 'string' ? option : option.name
            )).filter(Boolean);
        };

        prototype._pbOptionValues = function optionValues(product, name, names) {
            const index = names.findIndex((item) => normalize(item) === normalize(name));
            if (index < 0) return [];
            return [...new Set((product.variants || []).map((variant) => {
                const values = Array.isArray(variant.options)
                    ? variant.options
                    : names.map((_, position) => variant[`option${position + 1}`]);
                return values[index];
            }).filter(Boolean))];
        };

        prototype._pbQuantityOption = function quantityOption(product, configuredName) {
            const names = this._pbOptionNames(product);
            const exact = names.find((name) => normalize(name) === normalize(configuredName));
            if (exact) return {
                name: exact,
                values: this._pbOptionValues(product, exact, names)
            };
            const candidates = names.map((name) => ({
                name,
                values: this._pbOptionValues(product, name, names)
            })).filter(({ values }) => (
                values.length && values.every((value) => range(value).valid)
            ));
            return candidates.length === 1 ? candidates[0] : null;
        };

        prototype._pbVariantValues = function variantValues(variant, names) {
            const raw = Array.isArray(variant.options)
                ? variant.options
                : names.map((_, index) => variant[`option${index + 1}`]);
            return Object.fromEntries(names.map((name, index) => [name, raw[index] ?? '']));
        };

        prototype._pbMetadata = async function metadata(line) {
            const product = await this._pbProduct(line);
            const names = this._pbOptionNames(product);
            const properties = line.properties || {};
            const quantityOption = this._pbQuantityOption(product, properties[QUANTITY_OPTION]);
            if (!quantityOption) throw new Error('No se pudo identificar la escala del producto.');

            const packages = positive(properties[PACKAGES]);
            const units = positive(properties[UNITS]);
            const increment = positive(properties[INCREMENT])
                || (packages && units && units % packages === 0 ? units / packages : null);
            if (!Number.isInteger(increment) || increment < 1) {
                throw new Error('No se pudo identificar el incremento del producto.');
            }

            const lineOptions = line.options_with_values || [];
            const commercial = {};
            names.forEach((name) => {
                if (normalize(name) === normalize(quantityOption.name)) return;
                commercial[name] = lineOptions.find(
                    (option) => normalize(option.name) === normalize(name)
                )?.value ?? '';
            });

            const productId = String(line.product_id ?? properties[PRODUCT_ID] ?? '');
            const group = String(properties[GROUP] || '')
                || this._pbDerivedGroup(line, quantityOption.name);
            return { line, product, names, quantityOption, increment, commercial, productId, group };
        };

        prototype._pbBuild = function build(metadata, packages, sellingPlan) {
            const units = packages * metadata.increment;
            const selectedRange = metadata.quantityOption.values
                .map(range)
                .find((item) => item.valid
                    && units >= item.from
                    && (item.to === null || units <= item.to));
            if (!selectedRange) throw new Error('La cantidad no pertenece a una escala válida.');

            const variant = (metadata.product.variants || []).find((candidate) => {
                const values = this._pbVariantValues(candidate, metadata.names);
                return metadata.names.every((name) => {
                    const expected = normalize(name) === normalize(metadata.quantityOption.name)
                        ? selectedRange.value
                        : metadata.commercial[name];
                    return normalize(values[name]) === normalize(expected);
                });
            });
            if (!variant) throw new Error('No existe una variante para la nueva escala.');
            if (variant.available === false) throw new Error('La nueva escala no está disponible.');

            const properties = {
                ...(metadata.line.properties || {}),
                [PACKAGES]: String(packages),
                [UNITS]: String(units),
                [TIER]: selectedRange.value,
                [GROUP]: metadata.group,
                [PRODUCT_ID]: metadata.productId,
                [QUANTITY_OPTION]: metadata.quantityOption.name,
                [INCREMENT]: String(metadata.increment)
            };
            delete properties[metadata.quantityOption.name];

            return {
                id: variant.id,
                quantity: packages,
                selling_plan: sellingPlan,
                properties
            };
        };

        prototype._pbRemove = async function remove(metadata) {
            const context = {
                group: metadata.group,
                productId: metadata.productId,
                quantityOption: metadata.quantityOption.name,
                increment: metadata.increment
            };
            let cartData = await this._pbCart();

            for (let guard = 0; guard < 20; guard += 1) {
                const cart = await this._pbCart();
                const lines = this._pbLines(cart, context);
                if (!lines.length) return cartData;
                const sections = lines.length === 1 && this.features.cart ? [CART_SECTION] : [];
                cartData = await this._pbChange(lines[0].key, 0, undefined, undefined, sections);
            }

            throw new Error('No fue posible eliminar la selección configurada.');
        };

        prototype._pbError = function cartError(error) {
            console.error('Purabox: no se pudo actualizar el carrito.', error);
            let message = '';
            let description = '';
            try {
                ({ message = '', description = '' } = JSON.parse(error.message));
            } catch (parseError) {
                message = error.message || 'No se pudo actualizar el carrito';
            }
            this.publish('toast-notification:open', {
                title: message || 'No se pudo actualizar el carrito',
                message: description,
                type: 'error'
            });
            this.publish('cart:error');
        };

        prototype._add = async function add({ items }) {
            const list = Array.isArray(items) ? items : [];
            const configured = list.find((item) => this._pbConfigured(item));
            if (!configured) return nativeAdd.call(this, { items: list });

            try {
                let result = await this._pbConverge(configured);
                const remaining = list.filter((item) => item !== configured);
                if (remaining.length) {
                    await this._pbRequest('cartAdd', { items: remaining });
                    const cart = await this._pbCart();
                    const lines = this._pbLines(cart, result.context);
                    const target = this._pbTarget(lines, configured.id, configured.properties);
                    if (!target) throw new Error('No se pudo localizar la selección configurada.');
                    result = { cart, target, context: result.context };
                }

                if (this.redirectToCart) {
                    location.pathname = '/cart';
                    return;
                }

                const cartData = await this._pbRender(
                    result,
                    list.map(({ id }) => id),
                    true
                );
                this._publishUpdates(cartData);
            } catch (error) {
                this._pbError(error);
            }
        };

        prototype._update = async function update(payload) {
            const { key, quantity, selling_plan } = payload || {};
            try {
                const cart = await this._pbCart();
                const line = (cart.items || []).find(
                    (item) => String(item.key) === String(key)
                );
                if (!line || normalize(line.properties?.[MODE]) !== 'rangos') {
                    return nativeUpdate.call(this, payload);
                }

                const desired = integer(quantity);
                if (!Number.isInteger(desired) || desired < 0) {
                    throw new Error('La cantidad seleccionada no es válida.');
                }

                const metadata = await this._pbMetadata(line);
                if (desired === 0) {
                    const cartData = await this._pbRemove(metadata);
                    this._publishUpdates(cartData);
                    return;
                }

                const targetItem = this._pbBuild(metadata, desired, selling_plan || '');
                const result = await this._pbConverge(targetItem);
                const cartData = await this._pbRender(result, [], false);
                this._publishUpdates(cartData);
            } catch (error) {
                this._pbError(error);
            }
        };
    }).catch((error) => {
        console.error('Purabox: no se pudo iniciar el control de rangos.', error);
    });
})();
